import { I18nProvider } from './i18n/context/I18nProvider';
import React, { useState, useReducer, useEffect } from 'react';

import { MatchState } from './types';
import { convertToMatchInfo } from './utils';
import { matchReducer } from './store/matchReducer';
import { DEFAULT_PLAYER_COLORS } from './constants';

import { AppShell } from './components/AppShell';
import { useMatchPersistence } from './hooks/useMatchPersistence';
import { useMatchRecovery } from './hooks/useMatchRecovery';
import { useScreenNavigation } from './hooks/useScreenNavigation';
import { useMatchData } from './hooks/useMatchData';
import { useCourtPairing } from './hooks/useCourtPairing';
import { AppMode, useAppConfig } from './hooks/useAppConfig';
import { useWakeLock } from './hooks/useWakeLock';
import { useFavicon } from './hooks/useFavicon';

import { PWAWrapper } from './components/PWAManager';
import { usePWA } from './hooks/usePWA';
import { SimpleUpdateBanner } from './components/SimpleUpdateBanner';
import { ChangelogProvider } from './components/ChangelogProvider';
import { BrandingProvider } from './branding';
import * as matchStorage from './storage/matchStorage';

// Fonction pour créer le state initial à partir des données validées
function createInitialState(matchData: any): MatchState {
  const matchInfo = convertToMatchInfo(matchData);
  
  return {
    // Match info depuis les données d'entrée
    matchInfo,
    
    // Current screen
    currentScreen: 'before',
    
    // Player colors (valeurs par défaut)
    playerAColor: DEFAULT_PLAYER_COLORS.A,
    playerBColor: DEFAULT_PLAYER_COLORS.B,
    
    // Serving info
    servingPlayer: null,
    servingSide: null,
    canChangeServingSide: true,
    
    // Scores
    gameScores: { A: 0, B: 0 },
    setScores: { A: 0, B: 0 },
    
    // Match state
    matchStarted: false,
    matchEnded: false,
    winner: null,
    
    // Game history for rollback
    history: [],
    
    // Points table for current game
    pointsTable: [],
    
    // Pending decision states
    pendingDecision: null,
    pendingDecisionLineIndex: null,

    // Match duration tracking
    matchStartTime: null,
    gameStartTime: null,
    totalMatchDuration: 0,
    currentGameDuration: 0,
    
    // Game results history
    gameResults: [],
    
    // Conduct sanctions tracking
    conductSanctions: []
  };
}

// État initial temporaire pour éviter les erreurs de reducer
const tempInitialState: MatchState = {
  matchInfo: {
    matchId: 'temp',
    competitionName: 'temp',
    categoryName: 'temp',
    drawName: 'temp',
    refereeName: 'temp',
    dateTime: 'temp',
    courtName: 'temp',
    scoringType: 'BestOf5',
    playerA: 'temp',
    playerB: 'temp'
  },
  currentScreen: 'before',
  playerAColor: DEFAULT_PLAYER_COLORS.A,
  playerBColor: DEFAULT_PLAYER_COLORS.B,
  servingPlayer: null,
  servingSide: null,
  canChangeServingSide: true,
  gameScores: { A: 0, B: 0 },
  setScores: { A: 0, B: 0 },
  matchStarted: false,
  matchEnded: false,
  winner: null,
  history: [],
  pointsTable: [],
  pendingDecision: null,
  pendingDecisionLineIndex: null,
  matchStartTime: null,
  gameStartTime: null,
  totalMatchDuration: 0,
  currentGameDuration: 0,
  gameResults: [],
  conductSanctions: []
};

// Main App Component
export default function SquashScoringApp() {
  const {
    matchData,
    setupData,
    validationErrors,
    isValidating,
    hasErrors,
    isInitialized,
    dataSource,
    retry,
    clearData
  } = useMatchData();
  
  // State du match - initialisé avec un état temporaire puis mis à jour
  const [matchState, dispatch] = useReducer(matchReducer, tempInitialState);
  const [realStateInitialized, setRealStateInitialized] = useState(false);
  
  // Hooks de persistance
  const { clearSavedMatch } = useMatchPersistence(matchState);
  
  // Hook de récupération
  const {
    savedMatch,
    showRecoveryModal,
    handleRestoreMatch,
    handleStartNewMatch
  } = useMatchRecovery({
    isInitialized,
    clearSavedMatch,
    onRestoreMatch: (restored) => {
      dispatch({ type: 'INIT_STATE' as any, state: restored });
      setRealStateInitialized(true);
    },
    onStartNewMatch: () => {
      if (dataSource && isInitialized && matchData) {
        // clearSavedMatch() vient d'effacer TOUTES les clés de match, y compris
        // le payload entrant. On le ré-écrit pour que le nouveau match survive
        // à un refresh avant son démarrage.
        matchStorage.saveMatchInput(matchData);
        const realInitialState = createInitialState(matchData);
        dispatch({ type: 'INIT_STATE' as any, state: realInitialState });
        setRealStateInitialized(true);
      } else {
        setForceSetupScreen(true);
      }
    }
  });

  // Navigation entre écrans
  const { screenToShow, setForceSetupScreen } = useScreenNavigation({
    dataSource,
    isInitialized,
    matchData,
    setupData,
    savedMatch,
    showRecoveryModal,
    realStateInitialized,
    matchState
  });

  // Tablette pairée avec un court : signale sa présence au backend, prend le match envoyé sur
  // son court quand elle est sur l'écran d'attente, et l'injecte dans le flux habituel
  // (storage puis relecture des sources).
  const pairing = useCourtPairing({
    idle: !isValidating && screenToShow === 'setup_screen' && !setupData,
    onMatchReceived: (match) => {
      matchStorage.clearSetupInput();
      matchStorage.saveMatchInput(match);
      retry();
    },
    // The center manager asked the tablet to drop its match: same path as "New match"
    onReset: () => dispatch({ type: 'START_NEW_MATCH' })
  });

  // A paired tablet runs as a kiosk: reduced menu, and the
  // result screen comes back to the waiting screen on its own
  const appMode: AppMode = pairing.pairedCourt ? 'paired' : 'standalone';
  const { config: appConfig } = useAppConfig();

  // Match manuel sur le court pairé : même chemin qu'un scan de QR code
  const startManualMatch = () => {
    const court = pairing.pairedCourt?.court;
    if (!court) return;
    matchStorage.saveSetupInput({
      matchId: 'LIVE_' + Array.from(crypto.getRandomValues(new Uint8Array(8)), b => b.toString(16).padStart(2, '0')).join('').toUpperCase(),
      ...court
    });
    retry();
  };

  // PWA hooks - valeurs disponibles pour usage futur
  usePWA();
  const wakeLock = useWakeLock();

  // Branding dynamique - utilise le favicon du branding si disponible
  useFavicon(matchState.matchInfo?.branding?.favicon);

  // Réinitialiser le state quand les données deviennent disponibles
  useEffect(() => {
    if (isInitialized && matchData && !realStateInitialized) {
      const realInitialState = createInitialState(matchData);
      dispatch({ type: 'INIT_STATE' as any, state: realInitialState });
      setRealStateInitialized(true);
    }
  }, [isInitialized, matchData, realStateInitialized]);

  // Reset si les données changent
  useEffect(() => {
    if (!isInitialized && realStateInitialized) {
      setRealStateInitialized(false);
    }
  }, [isInitialized, realStateInitialized]);

  // Protection contre le refresh pendant un match actif
  const isMatchActive = matchState.matchStarted && !matchState.matchEnded;

  // Rendu via AppShell
  return (
    <>
      <I18nProvider enablePersistence={true}>
        <PWAWrapper>
          <BrandingProvider branding={matchState.matchInfo?.branding}>
            <ChangelogProvider matchState={matchState} dispatch={dispatch} appMode={appMode}>
              <AppShell
                isValidating={isValidating}
                hasErrors={hasErrors}
                validationErrors={validationErrors}
                isInitialized={isInitialized}
                matchData={matchData}
                setupData={setupData}
                dataSource={dataSource}
                retry={retry}
                screenToShow={screenToShow}
                savedMatch={savedMatch}
                handleRestoreMatch={handleRestoreMatch}
                handleStartNewMatch={handleStartNewMatch}
                matchState={matchState}
                dispatch={dispatch}
                realStateInitialized={realStateInitialized}
                clearData={clearData}
                isMatchActive={isMatchActive}
                wakeLock={wakeLock}
                pairedCourt={pairing.pairedCourt}
                pairingLink={pairing.link}
                onManualMatch={appConfig.paired.allowManualMatch ? startManualMatch : undefined}
                appMode={appMode}
              />
            </ChangelogProvider>
          </BrandingProvider>
        </PWAWrapper>

        <SimpleUpdateBanner />
      </I18nProvider>
    </>
  );
}